"use client";

import type * as z from "zod";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { userAuthSchema } from "@/lib/validations/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { useSearchParams } from "next/navigation";
import FormError from "@/components/ui/form-error";
import FormSuccess from "@/components/ui/form-success";
import { FloatingLabelInput } from "@/components/ui/floating-label";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "next/navigation";

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {}

type FormData = z.infer<typeof userAuthSchema>;

export function SignInForm({ className, ...props }: UserAuthFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(userAuthSchema),
  });
  const { signIn } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>("");
  const [success, setSuccess] = useState<string | undefined>("");
  const searchParams = useSearchParams();
  const router = useRouter();
  const urlError = searchParams.get("error");

  useEffect(() => {
    if (urlError === "OAuthAccountNotLinked") {
      setError("Email already in use with another provider");
    }
  }, [urlError]);

  async function onSubmit(data: FormData) {
    setIsLoading(true);
    setError("");
    setSuccess("");

    if (!data.username || !data.password) {
      setError("Please enter a username and password");
      setIsLoading(false);
      return;
    }

    try {
      await signIn({
        username: data.username,
        password: data.password,
      }).then(() => {
        router.push("/");
      });
    } catch (error: any) {
      setError(error.message);
      setIsLoading(false);
    }
  }

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-3">
          <div className="grid gap-2">
            <FloatingLabelInput
              id="username"
              label="Username"
              {...register("username")}
            />
            {errors?.username && (
              <p className="px-1 text-xs text-red-600">
                {errors.username.message}
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <FloatingLabelInput
              id="password"
              label="Password"
              type="password"
              {...register("password")}
            />
            {errors?.password && (
              <p className="px-1 text-xs text-red-600">
                {errors.password.message}
              </p>
            )}
          </div>

          <FormError message={urlError ?? error} />
          <FormSuccess message={success} />

          <Button disabled={isLoading}>
            {isLoading && <LoadingSpinner />}
            Sign in
          </Button>
        </div>
      </form>
    </div>
  );
}
